import { api } from '@/lib/api/client';
import type {
  Expense,
  ExpenseFilters,
  ExpenseListResponse,
  CreateExpenseDto,
  UpdateExpenseDto,
  ExpenseStatus,
} from '../types';

export const expenseKeys = {
  all: ['expenses'] as const,
  lists: () => [...expenseKeys.all, 'list'] as const,
  list: (filters: ExpenseFilters) => [...expenseKeys.lists(), filters] as const,
  detail: (id: string | number) => [...expenseKeys.all, 'detail', id] as const,
};

function buildListParams(filters: ExpenseFilters): Record<string, unknown> {
  const limit = filters.limit ?? 20;
  const params: Record<string, unknown> = {
    limit,
    offset: (filters.page ?? 0) * limit,
  };
  if (filters.status) params['status'] = filters.status;
  if (filters.expenseCategoryId) params['expenseCategoryId'] = filters.expenseCategoryId;
  if (filters.matterId) params['matterId'] = filters.matterId;
  if (filters.contractId) params['contractId'] = filters.contractId;
  return params;
}

export const expensesApi = {
  getExpenses: async (filters: ExpenseFilters): Promise<ExpenseListResponse> => {
    return api.get<ExpenseListResponse>('/expenses', {
      params: buildListParams(filters),
    });
  },

  getExpense: async (id: string | number): Promise<Expense> => {
    return api.get<Expense>(`/expenses/${id}`);
  },

  createExpense: async (data: CreateExpenseDto): Promise<Expense> => {
    return api.post<Expense>('/expenses', data);
  },

  updateExpense: async (
    id: string | number,
    data: UpdateExpenseDto,
  ): Promise<Expense> => {
    return api.patch<Expense>(`/expenses/${id}`, data);
  },

  // Status workflow — NEW → PENDING → ACCEPTED / DENIED.
  setStatus: async (id: string | number, status: ExpenseStatus): Promise<Expense> => {
    const path =
      status === 'PENDING' ? 'pending' : status === 'ACCEPTED' ? 'accepted' : 'denied';
    return api.put<Expense>(`/expenses/${path}/${id}`, {});
  },

  deleteExpense: async (id: string | number): Promise<void> => {
    await api.delete(`/expenses/${id}`);
  },
};
